/**
 * Follows a job: appends new log lines and stops polling once the job is finished.
 *
 * The log endpoint is offset-based, so we must keep the offset locally — asking from 0 every time
 * would re-print the whole build on every tick.
 */

import { useEffect, useRef, useState } from 'react'

import type { Job } from '../../api/types'
import { useApi } from '../../lib/context'

export interface JobTail {
  job: Job | null
  log: string
  error: string | null
}

export function useJobTail(jobId: string | null, intervalMs = 1200): JobTail {
  const api = useApi()
  const [job, setJob] = useState<Job | null>(null)
  const [log, setLog] = useState('')
  const [error, setError] = useState<string | null>(null)
  const offset = useRef(0)

  useEffect(() => {
    offset.current = 0
    setJob(null)
    setLog('')
    setError(null)
    if (!jobId) return
    let alive = true
    let busy = false

    const tick = async () => {
      if (busy) return
      busy = true
      try {
        const page = await api.jobLog(jobId, offset.current)
        if (!alive) return
        if (page.lines.length) setLog((prev) => prev + page.lines.join('\n') + '\n')
        if (typeof page.next_offset === 'number') offset.current = page.next_offset
        const current = await api.job(jobId)
        if (!alive) return
        setJob(current)
        if (current.status === 'done' || current.status === 'error') {
          clearInterval(id)
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e))
      } finally {
        busy = false
      }
    }

    const id = setInterval(() => void tick(), intervalMs)
    void tick()
    return () => {
      alive = false
      clearInterval(id)
    }
  }, [api, jobId, intervalMs])

  return { job, log, error }
}
